import { useCallback, useRef, type MutableRefObject } from 'react';
import { useInstance } from '@milkdown/react';
import { editorViewCtx, parserCtx } from '@milkdown/kit/core';
import { Slice } from '@milkdown/kit/prose/model';
import type { EditorView } from '@milkdown/kit/prose/view';
import { ySyncPluginKey } from 'y-prosemirror';

import { getHeadings, headingsEqual, type Heading } from './lib/getHeadings';
import { normalizeMarkdown } from './normalizeMarkdown';

export { normalizeMarkdown } from './normalizeMarkdown';

export type ReplaceContentFn = (markdown: string) => void;

/** Transaction-like shape for post-dispatch callback (avoids PM import). */
export type PostDispatchFn = (tr: { docChanged?: boolean }) => void;

interface UseEditorReadyOptions {
  /** Ref to be set with replaceContent function when editor is ready */
  replaceContentRef?: MutableRefObject<ReplaceContentFn | null>;
  /** Ref to be set with refreshHeadings function when editor is ready */
  refreshHeadingsRef?: MutableRefObject<(() => void) | null>;
  /** Standalone: view wired. Collab: fired from setupCollaboration after Y.Doc apply. */
  onReady?: () => void;
  /** Called when document headings change (on ready and content updates) */
  onHeadingsChange?: (headings: Heading[]) => void;
  /** Ref holding the collab service — disconnected on cleanup to prevent late dispatches */
  collabServiceRef?: MutableRefObject<{ disconnect(): unknown } | null>;
  /**
   * When set (e.g. when collab is enabled), register post-dispatch logic here instead of
   * wrapping view.dispatch. Caller installs a single wrapper that calls this after each dispatch.
   */
  postDispatchRef?: MutableRefObject<PostDispatchFn | null>;
  /** Live ProseMirror view for slash-menu gating (cleared on detach). */
  editorViewRef?: MutableRefObject<EditorView | null>;
  /** Live selected channel for variable-content heading resolution. */
  selectedChannelIdRef?: MutableRefObject<string | undefined>;
}

/**
 * Hook that runs **synchronously** when the Milkdown editor's container mounts.
 *
 * Returns a callback ref to attach to the container div. Unlike a useEffect-based
 * approach, this runs during the React commit phase — before any async effects or
 * scheduled callbacks — so the dispatch wrapper is in place before collab can fire.
 *
 * Responsibilities:
 * 1. Exposes ProseMirror view on DOM element (for testing)
 * 2. Provides replaceContent function (for revision restore)
 * 3. Tracks headings and fires onHeadingsChange callback (for TOC)
 * 4. When collab: registers post-dispatch via postDispatchRef (single wrapper in EditorCore).
 *    When no collab: wraps view.dispatch to track heading changes and guard teardown races.
 */
export function useEditorReady({
  replaceContentRef,
  refreshHeadingsRef,
  onReady,
  onHeadingsChange,
  collabServiceRef,
  postDispatchRef,
  editorViewRef,
  selectedChannelIdRef,
}: UseEditorReadyOptions) {
  const [loading, getEditor] = useInstance();

  // Refs to avoid stale closures in callbacks
  const previousHeadingsRef = useRef<Heading[]>([]);
  const onHeadingsChangeRef = useRef(onHeadingsChange);
  onHeadingsChangeRef.current = onHeadingsChange;
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  // Track cleanup function from the previous attach so we can tear down
  // when the node detaches or a new node attaches (React 18 has no native
  // cleanup refs — React 19 does, but we support 18).
  const cleanupRef = useRef<(() => void) | null>(null);

  const callbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      // ── DETACH: previous node removed ──────────────────────────────────
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }

      if (!node || loading) return;
      const editor = getEditor();
      if (!editor) return;

      let view;
      try {
        view = editor.action((ctx) => ctx.get(editorViewCtx));
      } catch {
        // Editor contexts not ready yet (e.g. stale instance after room switch)
        return;
      }
      if (!view) return;

      // Guard flag — set to true on cleanup so closures over this editor/view
      // instance silently no-op instead of throwing "Context not found" errors
      // when Milkdown tears down contexts before React runs cleanup.
      let destroyed = false;

      // ─────────────────────────────────────────────────────────────────────
      // 1. TESTING: Expose ProseMirror view on DOM for Storybook tests
      // ─────────────────────────────────────────────────────────────────────
      (node as any).pmView = view;
      if (editorViewRef) {
        editorViewRef.current = view;
      }

      // ─────────────────────────────────────────────────────────────────────
      // 2. REPLACE CONTENT: Function to swap entire editor content (revision restore)
      // ─────────────────────────────────────────────────────────────────────
      if (replaceContentRef) {
        replaceContentRef.current = (markdown: string) => {
          if (destroyed) return;
          const normalized = normalizeMarkdown(markdown);
          editor.action((ctx) => {
            const currentView = ctx.get(editorViewCtx);
            const parser = ctx.get(parserCtx);
            const parsedDoc = parser(normalized);
            if (parsedDoc) {
              const ystate = ySyncPluginKey.getState(currentView.state) as
                | {
                    type?: {
                      length: number;
                      delete: (start: number, length: number) => void;
                      doc?: {
                        transact: (fn: () => void, origin?: unknown) => void;
                      };
                    };
                  }
                | undefined;

              // Collab restore: clear the bound XmlFragment first so the following
              // PM replace is an insert into an empty Y fragment. That avoids
              // updateYFragment OOMing against Platform-shaped tables (CSUP-1211),
              // while still running appendTransaction normalizers (Milkdown marks
              // → Platform `code_inline` / `em` / etc.) before y-prosemirror syncs.
              if (ystate?.type?.doc) {
                const fragment = ystate.type;
                fragment.doc!.transact(() => {
                  if (fragment.length > 0) {
                    fragment.delete(0, fragment.length);
                  }
                }, ySyncPluginKey);

                const tr = currentView.state.tr.replace(0, currentView.state.doc.content.size, new Slice(parsedDoc.content, 0, 0));
                currentView.dispatch(tr);
                
return;
              }

              const { doc } = currentView.state;
              const tr = currentView.state.tr.replace(0, doc.content.size, new Slice(parsedDoc.content, 0, 0));
              currentView.dispatch(tr);
            }
          });
        };
      }

      // ─────────────────────────────────────────────────────────────────────
      // 3. TOC HEADINGS: Extract headings and notify when they change
      // ─────────────────────────────────────────────────────────────────────
      //
      // How TOC sync works:
      // - On editor ready: extract headings → fire callback → TOC renders
      // - On every edit: check if headings changed → fire callback if different
      // - On collab sync: parent calls refreshHeadings() → forces re-extraction
      // - On selectedChannelId change: EditorCore calls refreshHeadings() → channel-filtered TOC
      //
      const updateHeadings = () => {
        if (destroyed || !onHeadingsChangeRef.current) {
          return undefined;
        }
        let currentView;
        try {
          currentView = editor.action((ctx) => ctx.get(editorViewCtx));
        } catch (e) {
          if (e instanceof Error && e.message.includes('not found')) {
            return undefined;
          }
          throw e;
        }
        const headings = getHeadings(currentView.state.doc, {
          selectedChannelId: selectedChannelIdRef?.current,
        });

        // Only fire callback if headings actually changed (avoids re-renders)
        if (!headingsEqual(headings, previousHeadingsRef.current)) {
          previousHeadingsRef.current = headings;
          onHeadingsChangeRef.current(headings);
        }
      };

      // Expose for manual triggering (needed after collab sync settles)
      if (refreshHeadingsRef) {
        refreshHeadingsRef.current = updateHeadings;
      }

      // Initial extraction
      updateHeadings();
      onReadyRef.current?.();

      // ─────────────────────────────────────────────────────────────────────
      // 4. DISPATCH: single wrapper when collab (in EditorCore), or local wrapper when no collab
      // ─────────────────────────────────────────────────────────────────────
      const postDispatch: PostDispatchFn = (tr) => {
        if (destroyed) return;
        if (tr.docChanged) updateHeadings();
      };

      if (postDispatchRef) {
        // Collab: caller has one wrapper that calls postDispatchRef after each dispatch.
        postDispatchRef.current = postDispatch;
      } else {
        // No collab: wrap view.dispatch here to track heading changes and guard teardown.
        const originalDispatch = view.dispatch.bind(view);
        view.dispatch = (tr) => {
          if (destroyed) return;
          originalDispatch(tr);
          if (!destroyed && tr.docChanged) updateHeadings();
        };
      }

      // ─────────────────────────────────────────────────────────────────────
      // CLEANUP — stored in ref, called on detach or re-attach
      // ─────────────────────────────────────────────────────────────────────
      cleanupRef.current = () => {
        destroyed = true;
        if (postDispatchRef) postDispatchRef.current = null;
        // Disconnect collab service first — removes y-prosemirror's Y.Doc observer,
        // preventing late dispatches that would hit dead Milkdown contexts.
        if (collabServiceRef?.current) {
          try {
            collabServiceRef.current.disconnect();
          } catch (e) {
            // Context may already be torn down on unmount; swallow "not found" so cleanup can finish.
            const msg = e instanceof Error ? e.message : String(e);
            if (!msg.includes('not found')) throw e;
          }
          collabServiceRef.current = null;
        }
        if (replaceContentRef) replaceContentRef.current = null;
        if (refreshHeadingsRef) refreshHeadingsRef.current = null;
        if (editorViewRef) editorViewRef.current = null;
        // Replace dispatch with no-op instead of restoring original.
        // The original Milkdown-wrapped dispatch hits dead contexts after teardown,
        // but late dispatches from Y.js collab sync can still arrive on this view.
        view.dispatch = () => {};
      };
    },
    [loading, getEditor, replaceContentRef, refreshHeadingsRef, onReady, postDispatchRef, collabServiceRef, editorViewRef, selectedChannelIdRef],
  );

  return callbackRef;
}
